import { Inject, Injectable } from '@angular/core';
import {
  CreateSnapshotWidgetRequestInterface,
  ListSnapshotWidgetRequestFiltersInterface,
  PagedResponse,
  SnapshotWidget,
  SnapshotWidgetInterface,
  SnapshotWidgetService,
  WidgetStylesInterface,
} from '@vendasta/snapshot-widget';
import { Observable } from 'rxjs';
import { map, share, switchMap } from 'rxjs/operators';

const PAGE_SIZE = 10;

@Injectable()
export class SnapshotWidgetApiService {
  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private widgetService: SnapshotWidgetService,
  ) {}

  public deleteSnapshotWidget(widgetId: string): Observable<boolean> {
    return this.partnerId$.pipe(switchMap((partnerId) => this.widgetService.delete(widgetId, partnerId)));
  }

  public createSnapshotWidget(widget: CreateSnapshotWidgetRequestInterface): Observable<SnapshotWidget> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.widgetService.create({
          ...widget,
          partnerId,
        });
      }),
    );
  }

  public getSnapshotWidget(widgetId: string): Observable<SnapshotWidget> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.widgetService.get(widgetId, partnerId);
      }),
    );
  }

  public getEmbedCode(widgetId: string, widgetStyles: WidgetStylesInterface): Observable<string> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.widgetService.getEmbedCode(widgetId, partnerId, widgetStyles);
      }),
    );
  }

  public updateSnapshotWidget(snapshotWidget: SnapshotWidgetInterface): Observable<boolean> {
    return this.widgetService.update(snapshotWidget);
  }

  public updateWidgetStyles(widgetId: string, widgetStyles: WidgetStylesInterface): Observable<boolean> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.widgetService.updateStyles(widgetId, partnerId, widgetStyles);
      }),
    );
  }

  public listSnapshotWidgets(
    marketId: string | null = null,
    nextCursor = '',
  ): Observable<PagedResponse<SnapshotWidget>> {
    return this.buildFilters(marketId).pipe(
      switchMap((lookupFilter) => this.widgetService.list(lookupFilter, nextCursor, PAGE_SIZE)),
    );
  }

  private buildFilters(marketId: string | null): Observable<ListSnapshotWidgetRequestFiltersInterface> {
    return this.partnerId$.pipe(
      map((partnerId) => {
        return {
          marketId: marketId,
          partnerId: partnerId,
        };
      }),
      share(),
    );
  }
}
